import {
  FileTextIcon,
  LockClosedIcon,
  ResetIcon,
  UpdateIcon,
} from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";

import {
  IJupyterRegistration,
  IJupyterRequeuePatientsResult,
  IJupyterSyncPatientResult,
  JupyterConnectionStatus,
} from "../../../@types/admin/AdminJupyterRegistration";
import { TableSorted } from "../../../components/TableSorted";
import {
  JupyterBulkSyncOutcome,
  useBulkSyncJupyterPatients,
  useRequeueJupyterPatients,
  useSyncJupyterPatient,
} from "../../../hooks/mutations/admin/jupyter";
import { useJupyterRegistrations } from "../../../hooks/queries/admin/jupyter";
import { useModal } from "../../../hooks/useModal";
import { useToast } from "../../../hooks/useToast";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  ErrorMessage,
  FilterButton,
  FiltersActions,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageTitle,
  RowAction,
  SearchInput,
  SelectAllCheckbox,
  SelectCell,
  SelectionCount,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import { RowActions } from "./AdminJupyterIntegration.styles";
import {
  canRequeueMessage,
  JupyterChannel,
  JupyterDrillDownModal,
} from "./JupyterDrillDownModal";

const SKIPPED_REASONS: Record<string, string> = {
  access_token_unavailable:
    "the JHE access token could not be refreshed, so nothing was attempted. Check the connection status and the API logs.",
  no_base_url:
    "no JHE base URL is configured for this connection's hostname, so nothing was attempted.",
  invalid_external_patient_id:
    "the connection has no valid JHE patient id, so nothing was attempted.",
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * Turns the on-demand sync counters into one sentence, so a zero result says
 * WHY it was zero — nothing due, everything failed, rows held for consent, or
 * the sync never reached the outbox (EN-5231).
 */
export function describeSyncResult(result: IJupyterSyncPatientResult): {
  message: string;
  tone: "success" | "error";
} {
  if (result.skippedReason) {
    return {
      message: `sync skipped — ${
        SKIPPED_REASONS[result.skippedReason] ?? result.skippedReason
      }`,
      tone: "error",
    };
  }

  const parts: string[] = [];
  if (result.itemsSent > 0)
    parts.push(`${plural(result.itemsSent, "item")} sent`);
  if (result.itemsFailed > 0) {
    parts.push(`${plural(result.itemsFailed, "item")} failed`);
  }
  if (result.itemsHeld > 0) {
    parts.push(
      `${plural(result.itemsHeld, "item")} held (scope no longer consented)`
    );
  }

  if (parts.length === 0) {
    return { message: "sync finished, nothing was due.", tone: "success" };
  }

  const hint =
    result.itemsFailed > 0 ? " Open the message to see its last error." : "";
  return {
    message: `sync finished: ${parts.join(", ")}.${hint}`,
    tone: result.itemsFailed > 0 ? "error" : "success",
  };
}

/** Failed / PermanentlyFailed messages across a registration's scopes and surveys. */
export function countRequeueableMessages(
  registration: IJupyterRegistration
): number {
  const groups = [...registration.scopes, ...registration.fhir];
  return groups.reduce(
    (total, group) =>
      total +
      group.messages.filter((message) => canRequeueMessage(message.status))
        .length,
    0
  );
}

/** One sentence for a bulk requeue: what moved, or that nothing needed to. */
export function describeRequeueResult(result: IJupyterRequeuePatientsResult): {
  message: string;
  tone: "success" | "error";
} {
  const total = result.observationsRequeued + result.questionnairesRequeued;
  const patients = result.patients.filter(
    (p) => p.observationsRequeued + p.questionnairesRequeued > 0
  ).length;

  if (total === 0) {
    return { message: "No failed messages to requeue.", tone: "success" };
  }

  return {
    message: `Requeued ${plural(total, "message")} for ${plural(
      patients,
      "patient"
    )}. Sync the patients to send them now, or the next scheduled run will.`,
    tone: "success",
  };
}

/**
 * Sums a bulk sync into one sentence — how many patients synced cleanly, and
 * which ones need a look (failed items, skipped, or the call itself failed).
 */
export function describeBulkSyncResult(outcomes: JupyterBulkSyncOutcome[]): {
  message: string;
  tone: "success" | "error";
} {
  let itemsSent = 0;
  const needAttention: number[] = [];

  outcomes.forEach((outcome) => {
    if ("error" in outcome) {
      needAttention.push(outcome.patientId);
      return;
    }
    itemsSent += outcome.result.itemsSent;
    if (outcome.result.skippedReason || outcome.result.itemsFailed > 0) {
      needAttention.push(outcome.patientId);
    }
  });

  const clean = outcomes.length - needAttention.length;
  const summary = `Synced ${plural(outcomes.length, "patient")}: ${plural(
    itemsSent,
    "item"
  )} sent`;

  if (needAttention.length === 0) {
    return { message: `${summary}.`, tone: "success" };
  }

  const needs = needAttention.length === 1 ? "needs" : "need";
  const who = needAttention.length === 1 ? "patient" : "patients";
  return {
    message: `${summary}; ${clean} clean, ${
      needAttention.length
    } ${needs} attention (${who} ${needAttention.join(", ")}). Sync ${
      needAttention.length === 1 ? "it" : "each"
    } on its own to see why.`,
    tone: "error",
  };
}

/** Search covers what an operator would paste in: ids, names, MRN, status. */
const SEARCH_FIELDS: (keyof IJupyterRegistration)[] = [
  "integrationId",
  "patientId",
  "patientName",
  "hospital",
  "mrn",
  "status",
];

function connectionStatusTone(
  status: JupyterConnectionStatus
): "active" | "warning" | "inactive" {
  if (status === "Connected") return "active";
  if (status === "Pending") return "warning";
  return "inactive";
}

/**
 * JupyterHealth Exchange registrations (EN-5149) — migrated from the Retool
 * `page_JupyterIntegration` app. Read-only: one call to
 * `/Retool/JupyterHealthExchange/Registrations` returns every connection with
 * its OMH scopes and FHIR surveys inline, and the per-row Scopes / FHIR
 * buttons open the drill-down over that same data.
 *
 * (The Retool export's screen is titled "eMED Beaumont Integration" with a
 * matching urlSlug — a copy-paste leftover from the app it was cloned from,
 * not this page's identity.)
 */
export function AdminJupyterIntegration() {
  document.title = "Jupyter integration - Admin";

  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();
  const { registrations, isLoading, isError } = useJupyterRegistrations();
  const { syncPatient, syncingPatientId } = useSyncJupyterPatient();
  const { requeuePatients, isRequeueing, requeueingPatientIds } =
    useRequeueJupyterPatients();
  const { bulkSync, isBulkSyncing, bulkSyncingPatientIds } =
    useBulkSyncJupyterPatients();

  // Selected patient ids. Only Connected registrations can be selected — sync
  // answers 404 for anything else, and requeueing for a revoked connection
  // would only park the messages again.
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  const isBusy =
    isBulkSyncing || isRequeueing || syncingPatientId !== undefined;

  // "Sync now": run this patient's JHE sync immediately instead of waiting for
  // the ward's daily job. Pairs with Requeue in the drill-down — requeue the
  // message, sync the patient, and it is delivered within seconds.
  const handleSyncNow = async (registration: IJupyterRegistration) => {
    try {
      const result = await syncPatient(registration.patientId);
      const { message, tone } = describeSyncResult(result);
      showToast(`Patient ${registration.patientId}: ${message}`, tone);
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          `There was an error syncing patient ${registration.patientId}.`
        ),
        "error"
      );
    }
  };

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({
    data: registrations,
    searchFields: SEARCH_FIELDS,
  });

  const isSelectable = (registration: IJupyterRegistration) =>
    registration.status === "Connected";

  // A refetch can drop a registration or change its status; act only on the
  // selected rows that are still here and still selectable.
  const selectedRegistrations = useMemo(
    () =>
      registrations.filter((r) => selected.has(r.patientId) && isSelectable(r)),
    [registrations, selected]
  );
  const selectedFailedCount = selectedRegistrations.reduce(
    (total, r) => total + countRequeueableMessages(r),
    0
  );

  const toggleOne = useCallback((patientId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(patientId)) next.delete(patientId);
      else next.add(patientId);
      return next;
    });
  }, []);

  const selectablePageItems = items.filter(isSelectable);
  const pageAllSelected =
    selectablePageItems.length > 0 &&
    selectablePageItems.every((r) => selected.has(r.patientId));
  const pageSomeSelected = selectablePageItems.some((r) =>
    selected.has(r.patientId)
  );

  const togglePage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      selectablePageItems.forEach((r) => {
        if (pageAllSelected) next.delete(r.patientId);
        else next.add(r.patientId);
      });
      return next;
    });
  };

  const handleRequeue = async (patientIds: number[], label: string) => {
    try {
      const result = await requeuePatients(patientIds);
      const { message, tone } = describeRequeueResult(result);
      showToast(`${label}: ${message}`, tone);
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          `There was an error requeueing messages for ${label.toLowerCase()}.`
        ),
        "error"
      );
    }
  };

  const handleBulkSync = async () => {
    const patientIds = selectedRegistrations.map((r) => r.patientId);
    const outcomes = await bulkSync(patientIds);
    const { message, tone } = describeBulkSyncResult(outcomes);
    showToast(message, tone);
  };

  const openDrillDown = (
    registration: IJupyterRegistration,
    channel: JupyterChannel
  ) => {
    openModal(
      <JupyterDrillDownModal
        registration={registration}
        channel={channel}
        onClose={closeModal}
      />,
      { width: "min(1000px, 94vw)", maxHeight: "90vh", showCloseButton: true }
    );
  };

  const columns: ColumnDef<IJupyterRegistration, string>[] = useMemo(
    () => [
      {
        id: "select",
        // No accessor, so the table would print its "-" placeholder instead
        // of the checkbox without this opt-out.
        meta: { renderEmpty: true },
        header: () => (
          <SelectAllCheckbox
            aria-label="Select all connected patients on this page"
            checked={pageAllSelected}
            indeterminate={!pageAllSelected && pageSomeSelected}
            disabled={isBusy || selectablePageItems.length === 0}
            onChange={togglePage}
          />
        ),
        cell: ({ row }) => (
          <SelectCell
            type="checkbox"
            aria-label={`Select patient ${row.original.patientId}`}
            checked={selected.has(row.original.patientId)}
            disabled={isBusy || !isSelectable(row.original)}
            onClick={(event) => event.stopPropagation()}
            onChange={() => toggleOne(row.original.patientId)}
          />
        ),
        enableSorting: false,
      },
      {
        accessorKey: "integrationId",
        header: "Integration ID",
        cell: ({ row }) => <span>{row.original.integrationId}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "patientId",
        header: "Patient ID",
        cell: ({ row }) => <span>{row.original.patientId}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "patientName",
        header: "Patient name",
        cell: ({ row }) => <span>{row.original.patientName || "-"}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "hospital",
        header: "Hospital",
        cell: ({ row }) => <span>{row.original.hospital || "-"}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "mrn",
        header: "MRN",
        cell: ({ row }) => <span>{row.original.mrn || "-"}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "created",
        header: "Created",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <StatusBadge status={connectionStatusTone(row.original.status)}>
            {row.original.status}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => {
          const { scopes, fhir, status, patientId } = row.original;
          const unsynced = row.original.unsyncedConsentedScopes ?? [];
          const isSyncingThisPatient =
            syncingPatientId === patientId ||
            bulkSyncingPatientIds.has(patientId);
          const isRequeueingThisPatient =
            requeueingPatientIds?.includes(patientId) ?? false;
          const failedCount = countRequeueableMessages(row.original);
          return (
            <RowActions>
              {/* Only a Connected registration can be synced — the API answers 404
                  for anything else — and only one row at a time shows busy. */}
              <RowAction
                label={isSyncingThisPatient ? "Syncing…" : "Sync now"}
                icon={<UpdateIcon />}
                disabled={status !== "Connected" || isBusy}
                onClick={() => handleSyncNow(row.original)}
              />
              {/* Every Failed / PermanentlyFailed message for this patient, in
                  one go — the drill-down's Requeue handles a single message. */}
              <RowAction
                label={
                  isRequeueingThisPatient
                    ? "Requeueing…"
                    : `Requeue failed (${failedCount})`
                }
                icon={<ResetIcon />}
                disabled={status !== "Connected" || failedCount === 0 || isBusy}
                onClick={() =>
                  handleRequeue([patientId], `Patient ${patientId}`)
                }
              />
              {/* Retool hid each action when its collection was empty; a
                  disabled button says the same thing without the row's
                  controls jumping around.

                  These two labels are the only row actions that carried data
                  rather than a verb, so the count travels into the tooltip —
                  the disabled state still says "none" at a glance.

                  EN-5230: consented scopes we do not sync are counted in the
                  label and keep the button enabled, so a patient whose only
                  consents are unsynced is not shown as having "no scopes". */}
              <RowAction
                label={
                  unsynced.length > 0
                    ? `Scopes (${scopes.length}, ${unsynced.length} not synced)`
                    : `Scopes (${scopes.length})`
                }
                icon={<LockClosedIcon />}
                disabled={scopes.length === 0 && unsynced.length === 0}
                onClick={() => openDrillDown(row.original, "scopes")}
              />
              <RowAction
                label={`FHIR (${fhir.length})`}
                icon={<FileTextIcon />}
                disabled={fhir.length === 0}
                onClick={() => openDrillDown(row.original, "fhir")}
              />
            </RowActions>
          );
        },
      },
    ],
    [
      registrations,
      items,
      syncingPatientId,
      bulkSyncingPatientIds,
      requeueingPatientIds,
      selected,
      pageAllSelected,
      pageSomeSelected,
      isBusy,
      toggleOne,
    ]
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Jupyter integration</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by integration ID, patient, hospital, MRN, or status..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FiltersActions>
          <SelectionCount data-testid="jhe-selection-count">
            {selectedRegistrations.length === 0
              ? "No patients selected"
              : `${plural(selectedRegistrations.length, "patient")} selected`}
          </SelectionCount>
          <FilterButton
            variant="secondary"
            disabled={isBusy || selectedFailedCount === 0}
            onClick={() =>
              handleRequeue(
                selectedRegistrations.map((r) => r.patientId),
                "Selected patients"
              )
            }
            data-testid="jhe-bulk-requeue"
          >
            {isRequeueing
              ? "Requeueing…"
              : `Requeue selected (${plural(
                  selectedFailedCount,
                  "failed message"
                )})`}
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isBusy || selectedRegistrations.length === 0}
            onClick={handleBulkSync}
            data-testid="jhe-bulk-sync"
          >
            {isBulkSyncing
              ? `Syncing… (${bulkSyncingPatientIds.size} left)`
              : `Sync selected (${selectedRegistrations.length})`}
          </FilterButton>
        </FiltersActions>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load JupyterHealth Exchange registrations. Try refreshing
          the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-jupyter-registrations"]]}>
          <TableSorted
            columns={columns}
            data={items}
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "jupyter-registrations",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PageContainer>
  );
}
